import { Grid, Typography, Box, Paper, Chip, Fade, Grow } from "@mui/material";
import { useEffect, useState } from "react";
import {
    Control,
    FieldErrors,
    UseFormRegister,
    UseFormReset
} from "react-hook-form";
import { ErrorOutline as ErrorIcon, Info as InfoIcon } from "@mui/icons-material";
import WSSelectField from "../../../Components/Select/WSSelect";

interface StatusProps {
    control: Control<{ newStatus: string }>;
    errors: FieldErrors;
    register: UseFormRegister<any>;
    id: any;
    reset: UseFormReset<any>;
    statusId?: string;
    config: any;
    currentStatus?: string;
    onStatusChange?: (newStatus: string) => void;
}

export const UpdateStatus: React.FC<StatusProps> = ({
    control,
    errors,
    register,
    id,
    reset,
    statusId,
    config,
    currentStatus,
    onStatusChange
}) => {
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [statusInfo, setStatusInfo] = useState<any>(null);

    useEffect(() => {
        reset();
        setSelectedStatus("");
    }, [id, reset]);

    const handleStatusChange = (value: string) => {
        setSelectedStatus(value);
        const selectedConfig = config.find((item: any) => item.value === value);
        setStatusInfo(selectedConfig);
        if (onStatusChange) {
            onStatusChange(value);
        }
    };

    const getStatusColor = (status: string) => {
        const statusColors: Record<string, { bg: string; color: string; border: string }> = {
            Pending: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
            Approved: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
            Rejected: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
            Draft: { bg: "#e0e7ff", color: "#3730a3", border: "#c7d2fe" },
            Completed: { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
            Cancelled: { bg: "#fef2f2", color: "#dc2626", border: "#fee2e2" },
        };
        return statusColors[status] || { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" };
    };

    const currentStatusColors = currentStatus ? getStatusColor(currentStatus) : getStatusColor("");

    return (
        <Fade in timeout={500}>
            <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                {/* Header Section */}
                {/* <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                            color: "#1f2937",
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <InfoIcon sx={{ color: "#6366f1", fontSize: { xs: 20, sm: 24 } }} />
                        Update Status
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#6b7280",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                    >
                        Change the status of this quotation. This action will be recorded in the audit log.
                    </Typography>
                </Box> */}

                {/* Current Status Display */}
                {currentStatus && (
                    <Grow in timeout={600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                borderRadius: "12px",
                                backgroundColor: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                transition: "all 0.2s ease",
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Typography variant="body2" fontWeight={500} color="#475569">
                                    Current Status:
                                </Typography>
                                <Chip
                                    label={currentStatus}
                                    sx={{
                                        bgcolor: currentStatusColors.bg,
                                        color: currentStatusColors.color,
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        px: 1,
                                        borderRadius: "20px",
                                        border: `1px solid ${currentStatusColors.border}`,
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grow>
                )}

                {/* Status Selection Form */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: "#374151",
                                    mb: 1,
                                    fontSize: "0.875rem",
                                }}
                            >
                                New Status <span style={{ color: "#ef4444" }}>*</span>
                            </Typography>
                        </Box>
                        <WSSelectField
                            options={config}
                            rules={{ required: true }}
                            {...register("newStatus", {
                                onChange: (e) => handleStatusChange(e.target.value)
                            })}
                            id="newStatus"
                            control={control}
                            label="Select Status"
                            name="newStatus"
                            labelType="Normal"
                        />
                        {errors.newStatus && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mt: 1,
                                }}
                            >
                                <ErrorIcon sx={{ fontSize: 14, color: "#ef4444" }} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: "#ef4444", fontWeight: 500 }}
                                >
                                    Status is required
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* Status Information Card */}
                    {statusInfo && (
                        <Grid item xs={12}>
                            <Grow in timeout={400}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        backgroundColor: "#f0fdf4",
                                        border: "1px solid #bbf7d0",
                                        mt: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#166534",
                                            fontWeight: 600,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        <InfoIcon sx={{ fontSize: 14 }} />
                                        Status Information
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#166534", mt: 0.5 }}>
                                        You are about to change the status to <strong>{statusInfo.label}</strong>.
                                        This action will update the quotation status and notify relevant stakeholders.
                                    </Typography>
                                </Box>
                            </Grow>
                        </Grid>
                    )}

                    {/* Warning Message for Critical Status Changes */}
                    {selectedStatus && (selectedStatus === "Rejected" || selectedStatus === "Cancelled") && (
                        <Grid item xs={12}>
                            <Fade in timeout={500}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        backgroundColor: "#fef2f2",
                                        border: "1px solid #fecaca",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                    }}
                                >
                                    <ErrorIcon sx={{ color: "#dc2626", fontSize: 18, mt: 0.2 }} />
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color="#991b1b">
                                            Warning!
                                        </Typography>
                                        <Typography variant="caption" color="#7f1d1d">
                                            Changing status to "{selectedStatus}" will lock this quotation and prevent any further modifications.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Fade>
                        </Grid>
                    )}
                </Grid>

                {/* Additional Information */}
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                    }}
                >
                    <Typography variant="caption" color="#6b7280" display="flex" alignItems="center" gap={0.5}>
                        <InfoIcon sx={{ fontSize: 14 }} />
                        Note: Status changes are tracked and cannot be undone. Please verify before confirming.
                    </Typography>
                </Box>
            </Box>
        </Fade>
    );
};